import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  Button,
  IconButton,
  Collapse,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import FilterListIcon from "@mui/icons-material/FilterList";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import MuiConfirmDialog from "../../components/mui/MuiConfirmDialog";
import { getProviders, patchProvider } from "../../helpers/api_helper";
import avatar from "../../assets/images/users/avatar-1.jpg";

const MuiProviderDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = "Provider Detail | FlexFlow ERP";

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Fetch provider data
  useEffect(() => {
    if (id) {
      setLoading(true);
      getProviders(id)
        .then((res) => {
          if (res && res[0]) {
            setProvider(res[0]);
          } else {
            setError("Provider not found");
          }
        })
        .catch(() => setError("Error fetching provider details"))
        .finally(() => setLoading(false));
    }
  }, [id]);

  // Process and filter transaction data
  const { filteredData, totalOrders, totalPayments, totalBalance } = useMemo(() => {
    if (!provider) {
      return {
        filteredData: [],
        totalOrders: 0,
        totalPayments: 0,
        totalBalance: 0,
      };
    }

    const orders = provider.orders || [];
    const payments = provider.payments || [];

    const fromTimestamp = dateFrom ? new Date(dateFrom).getTime() : 0;
    const toTimestamp = dateTo ? new Date(dateTo).getTime() : Infinity;

    const formattedOrders = orders
      .map((order) => {
        let buyAmount = 0;
        try {
          buyAmount = JSON.parse(order.totals)?.totalOrder || 0;
        } catch (e) {
          buyAmount = 0;
        }
        return {
          date: new Date(order.importDate).getTime(),
          type: "Order",
          payment: "",
          orderid: order.id,
          buy: buyAmount,
          refcode: order.refcode,
        };
      })
      .filter((entry) => entry.date >= fromTimestamp && entry.date <= toTimestamp);

    const formattedPayments = payments
      .map((payment) => ({
        date: new Date(payment.date).getTime(),
        type: "Payment",
        payment: -Math.abs(payment.ammount),
        buy: "",
        orderid: payment.id,
        refcode: payment.refcode,
      }))
      .filter((entry) => entry.date >= fromTimestamp && entry.date <= toTimestamp);

    const combinedData = [...formattedOrders, ...formattedPayments];

    // Apply sorting
    combinedData.sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    // Calculate totals
    const totalOrdersCalc = combinedData.reduce(
      (sum, entry) => sum + (entry.buy ? parseFloat(entry.buy) : 0),
      0
    );
    const totalPaymentsCalc = combinedData.reduce(
      (sum, entry) => sum + (entry.payment ? Math.abs(parseFloat(entry.payment)) : 0),
      0
    );
    const totalBalanceCalc = totalOrdersCalc - totalPaymentsCalc;

    return {
      filteredData: combinedData,
      totalOrders: totalOrdersCalc,
      totalPayments: totalPaymentsCalc,
      totalBalance: totalBalanceCalc,
    };
  }, [provider, dateFrom, dateTo, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (entry) => {
    if (entry.type !== "Payment") {
      navigate(`/invoice-detail/${entry.orderid}`);
    } else {
      navigate(`/payment-overview/${entry.orderid}`);
    }
  };

  const handleEdit = () => {
    setEditData({
      name: provider.name || "",
      label: provider.label || "",
      email: provider.email || "",
      phone: provider.phone || "",
      address: provider.address || "",
      city: provider.city || "",
      country: provider.country || "",
      vat: provider.vat || "",
      taxOffice: provider.taxOffice || "",
      profession: provider.profession || "",
      representor: provider.representor || "",
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        resource: [{
          id: parseInt(id),
          ...editData,
        }],
      };

      const res = await patchProvider(payload);
      if (res && res.error) {
        setSnackbar({ open: true, message: "Error updating provider", severity: "error" });
      } else {
        // Refresh provider data
        const response = await getProviders(id);
        if (response && response[0]) {
          setProvider(response[0]);
        }
        setSnackbar({ open: true, message: "Provider updated successfully", severity: "success" });
        setIsEditing(false);
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Error updating provider", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    // Implement delete logic here if needed
    setDeleteDialogOpen(false);
    navigate("/providers");
  };

  const breadcrumbs = [
    { label: "Providers", path: "/providers" },
    { label: provider?.name || "Provider Detail" },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Container maxWidth={false}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth={false}>
        <MuiPageHeader
          title="Provider Details"
          breadcrumbs={breadcrumbs}
          actions={
            <Box sx={{ display: "flex", gap: 1 }}>
              {isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveEdit}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Box>
          }
        />

        {provider && (
          <>
            {/* Provider Info Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3}>
                  {/* Provider Profile */}
                  <Grid item xs={12} md={6}>
                    {isEditing ? (
                      // Edit Mode
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          label="Provider Name"
                          value={editData.name}
                          onChange={(e) => handleEditFieldChange("name", e.target.value)}
                          fullWidth
                          size="small"
                          required
                        />
                        <TextField
                          label="Label / Company Name"
                          value={editData.label}
                          onChange={(e) => handleEditFieldChange("label", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <Divider />
                        <TextField
                          label="Email"
                          value={editData.email}
                          onChange={(e) => handleEditFieldChange("email", e.target.value)}
                          fullWidth
                          size="small"
                          type="email"
                        />
                        <TextField
                          label="Phone"
                          value={editData.phone}
                          onChange={(e) => handleEditFieldChange("phone", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Address"
                          value={editData.address}
                          onChange={(e) => handleEditFieldChange("address", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              label="City"
                              value={editData.city}
                              onChange={(e) => handleEditFieldChange("city", e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Country"
                              value={editData.country}
                              onChange={(e) => handleEditFieldChange("country", e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                        </Grid>
                        <TextField
                          label="VAT Number"
                          value={editData.vat}
                          onChange={(e) => handleEditFieldChange("vat", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Tax Office"
                          value={editData.taxOffice}
                          onChange={(e) => handleEditFieldChange("taxOffice", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Profession"
                          value={editData.profession}
                          onChange={(e) => handleEditFieldChange("profession", e.target.value)}
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Contact Person"
                          value={editData.representor}
                          onChange={(e) => handleEditFieldChange("representor", e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Box>
                    ) : (
                      // View Mode
                      <>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Avatar
                            src={avatar}
                            alt={provider.name}
                            sx={{ width: 80, height: 80 }}
                          />
                          <Box>
                            <Typography variant="h5" fontWeight={600}>
                              {provider.name}
                            </Typography>
                            {provider.label && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {provider.label}
                              </Typography>
                            )}
                            <Chip
                              label={`ID: #${provider.id}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Contact Details */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {provider.email && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <EmailIcon color="action" fontSize="small" />
                              <Typography variant="body2">{provider.email}</Typography>
                            </Box>
                          )}
                          {provider.phone && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <PhoneIcon color="action" fontSize="small" />
                              <Typography variant="body2">{provider.phone}</Typography>
                            </Box>
                          )}
                          {provider.address && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <LocationOnIcon color="action" fontSize="small" />
                              <Typography variant="body2">
                                {provider.address}
                                {provider.city && `, ${provider.city}`}
                                {provider.country && `, ${provider.country}`}
                              </Typography>
                            </Box>
                          )}
                          {provider.vat && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <BusinessIcon color="action" fontSize="small" />
                              <Typography variant="body2">VAT: {provider.vat}</Typography>
                            </Box>
                          )}
                          {provider.taxOffice && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <AccountBalanceIcon color="action" fontSize="small" />
                              <Typography variant="body2">
                                Tax Office: {provider.taxOffice}
                              </Typography>
                            </Box>
                          )}
                          {provider.profession && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <BusinessIcon color="action" fontSize="small" />
                              <Typography variant="body2">
                                Profession: {provider.profession}
                              </Typography>
                            </Box>
                          )}
                          {provider.representor && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <BusinessIcon color="action" fontSize="small" />
                              <Typography variant="body2">
                                Contact: {provider.representor}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}
                  </Grid>

                  {/* Statistics Cards */}
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Card
                          variant="outlined"
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: "error.50",
                            borderColor: "error.200",
                          }}
                        >
                          <PaymentIcon
                            color="error"
                            sx={{ fontSize: 32, mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Total Payments
                          </Typography>
                          <Typography variant="h5" color="error.main" fontWeight={600}>
                            {totalPayments.toFixed(2)} EUR
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card
                          variant="outlined"
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: "success.50",
                            borderColor: "success.200",
                          }}
                        >
                          <ReceiptIcon
                            color="success"
                            sx={{ fontSize: 32, mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Total Orders
                          </Typography>
                          <Typography
                            variant="h5"
                            color="success.main"
                            fontWeight={600}
                          >
                            {totalOrders.toFixed(2)} EUR
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card
                          variant="outlined"
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: "primary.50",
                            borderColor: "primary.200",
                          }}
                        >
                          <AccountBalanceIcon
                            color="primary"
                            sx={{ fontSize: 32, mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Balance
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary.main"
                            fontWeight={600}
                          >
                            {totalBalance.toFixed(2)} EUR
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Transaction History Card */}
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    Purchase History
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                  >
                    {isFilterOpen ? "Hide Filters" : "Filter"}
                  </Button>
                </Box>

                {/* Filter Section */}
                <Collapse in={isFilterOpen}>
                  <Box sx={{ mb: 3, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="From Date"
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="To Date"
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>

                {/* Transaction Table */}
                {filteredData.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 500 }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={sortField === "date"}
                              direction={sortField === "date" ? sortOrder : "asc"}
                              onClick={() => handleSort("date")}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={sortField === "payment"}
                              direction={sortField === "payment" ? sortOrder : "asc"}
                              onClick={() => handleSort("payment")}
                            >
                              Payment
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={sortField === "buy"}
                              direction={sortField === "buy" ? sortOrder : "asc"}
                              onClick={() => handleSort("buy")}
                            >
                              Purchase
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={sortField === "refcode"}
                              direction={sortField === "refcode" ? sortOrder : "asc"}
                              onClick={() => handleSort("refcode")}
                            >
                              Reference
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredData.map((entry, index) => (
                          <TableRow
                            key={index}
                            hover
                            onClick={() => handleRowClick(entry)}
                            sx={{
                              cursor: "pointer",
                              backgroundColor:
                                entry.type === "Payment"
                                  ? "error.50"
                                  : "success.50",
                              "&:hover": {
                                backgroundColor:
                                  entry.type === "Payment"
                                    ? "error.100"
                                    : "success.100",
                              },
                            }}
                          >
                            <TableCell>
                              {new Date(entry.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={entry.type}
                                size="small"
                                color={entry.type === "Payment" ? "error" : "success"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {entry.payment
                                ? `${entry.payment.toFixed(2)} EUR`
                                : "-"}
                            </TableCell>
                            <TableCell align="right">
                              {entry.buy ? `${entry.buy.toFixed(2)} EUR` : "-"}
                            </TableCell>
                            <TableCell>{entry.refcode || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    No transaction data found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <MuiConfirmDialog
          open={deleteDialogOpen}
          title="Delete Provider"
          content={`Are you sure you want to delete "${provider?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmColor="error"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
        />

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

MuiProviderDetail.propTypes = {};

export default MuiProviderDetail;
